import {useEffect, useState} from "react";
import {App as AntdApp, Select, Alert, Empty} from "antd";
import className from "classnames/bind";
import {Button, FontAwesomeIcon, Loading} from "~/components";
import {rtkErrorMessage} from "~/reduxs/api/apiSlice";
import {
	useGetGaPropertiesQuery,
	useSelectGaPropertyMutation,
	useDisconnectGaMutation,
} from "~/reduxs/api/gaApiSlice";
import style from "~/features/GscInsight/style/Gsc.module.scss";

const cn = className.bind(style);

/**
 * Chọn property GA4 để gắn vào dự án (mirror GscPropertyPicker). Property GA4 không mang
 * tên miền (chỉ "properties/123" + tên hiển thị) nên KHÔNG so khớp domain như GSC — user
 * tự chọn đúng property của website. Chọn xong BE đặt mốc backfill 13 tháng + sync nền.
 */
function GaPropertyPicker({projectId, email, onCancel}) {

	const {notification} = AntdApp.useApp();
	const {data: properties = [], isLoading, isError, error} = useGetGaPropertiesQuery(projectId);
	const [selectProperty, {isLoading: saving}] = useSelectGaPropertyMutation();
	const [disconnect] = useDisconnectGaMutation();
	const [property, setProperty] = useState(null);

	// Chỉ có đúng 1 property → tự chọn cho nhanh.
	useEffect(() => {
		if (properties.length === 1 && !property) setProperty(properties[0].property);
	}, [properties, property]);

	const handleSave = async () => {
		if (!property) {
			notification.warning({message: 'Chưa chọn', description: 'Vui lòng chọn một property GA4.'});
			return;
		}
		const chosen = properties.find((p) => p.property === property);
		try {
			await selectProperty({projectId, property, property_name: chosen?.name || ''}).unwrap();
			notification.success({message: 'Đã chọn property', description: 'Hệ thống đang đồng bộ dữ liệu trong nền.'});
		} catch (e) {
			notification.error({message: 'Lỗi', description: rtkErrorMessage(e, 'Không lưu được property')});
		}
	};

	const handleDisconnect = async () => {
		try {
			await disconnect(projectId).unwrap();
		} catch (e) {
			notification.error({message: 'Lỗi', description: rtkErrorMessage(e, 'Không ngắt được kết nối')});
		}
	};

	if (isLoading) return <Loading />;

	return (
		<div className={cn('connectPanel')}>
			<div className={cn('connectIcon')}>
				<FontAwesomeIcon icon="fa-light fa-list-check" />
			</div>
			<div className={cn('connectTitle')}>Chọn property GA4</div>
			<div className={cn('connectDesc')}>
				{email ? <>Đã kết nối bằng <strong>{email}</strong>. </> : null}
				Chọn property Google Analytics 4 của website để đồng bộ dữ liệu lưu lượng cho dự án này.
			</div>

			{isError && (
				<Alert type="error" showIcon style={{textAlign: 'left', marginBottom: 16}}
					message="Không lấy được danh sách property"
					description={rtkErrorMessage(error, 'Vui lòng thử ngắt kết nối và kết nối lại.')} />
			)}

			{!isError && properties.length === 0 && (
				<Empty description="Tài khoản Google này chưa có property GA4 nào." style={{marginBottom: 16}} />
			)}

			{properties.length > 0 && (
				<Select
					style={{width: '100%', maxWidth: 460, marginBottom: 20, textAlign: 'left'}}
					placeholder="Chọn property…"
					value={property}
					onChange={setProperty}
					showSearch
					optionFilterProp="label"
					options={properties.map((p) => ({
						value: p.property,
						label: `${p.name || p.property}${p.account ? ` — ${p.account}` : ''}`,
					}))}
				/>
			)}

			<div style={{display: 'flex', gap: 10, justifyContent: 'center'}}>
				{onCancel
					? <Button white onClick={onCancel}>Huỷ</Button>
					: <Button white onClick={handleDisconnect}>Ngắt kết nối</Button>}
				{properties.length > 0 && (
					<Button primary loading={saving} onClick={handleSave}
						leftIcon={<FontAwesomeIcon icon="fa-light fa-check" />}>
						Bắt đầu đồng bộ
					</Button>
				)}
			</div>
		</div>
	);
}

export default GaPropertyPicker;
